// 規則（ADR-011 §5；app-api /v1/homes/{id}/rules）：VMS 的規則編輯器與執行紀錄（RULE-01 / 02）。形狀照 platform internal/rules。
import { kinioFetch, planeBase } from '@kinio/web-sdk';

export interface RuleTrigger {
  type: string;
  [k: string]: unknown;
}
export interface RuleSpec {
  kind: 'automation' | 'scene';
  name: string;
  enabled?: boolean;
  mode?: 'single' | 'restart';
  triggers: RuleTrigger[];
  conditions: RuleTrigger[];
  actions: RuleTrigger[];
}
export interface Rule extends RuleSpec {
  rule_id: string;
  home_id: string;
  placement: 'cloud' | 'local';
  /** 本地執行的設備（平台欄名 executor_ref，值是 device_ref） */
  executor_ref?: string;
  owner_user_id: string;
  version: number;
  status?: {
    last_fired_at?: string;
    last_result?: 'ok' | 'failed' | 'skipped' | '';
    last_error?: { at: string; reason: string; action_index?: number };
    fires_total?: number;
    pending_sync?: boolean;
    /** 交接中（plan#35）：舊的本地執行設備還沒確認停用舊版；from 看不到時不帶；deleting = 刪除中（不能改、不能執行） */
    handoff?: { from?: string; since: string; deleting?: boolean };
  };
}
export interface RuleProblem {
  path: string;
  code: string;
  message: string;
}
export interface CatalogThing {
  device_ref: string;
  display_name?: string;
  events: string[];
  attrs: string[];
  commands: string[];
}
export interface RuleCatalog {
  things: CatalogThing[];
  action_types: string[];
}
export interface RuleRun {
  run_id: string;
  trigger_ref: string;
  status: string;
  next_action: number;
  actions: number;
  error?: { at: string; reason: string; action_index?: number };
  created_at: string;
  updated_at: string;
}

const base = (api: string, homeId: string) => `${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}/rules`;

export const rulesApi = {
  list: (api: string, homeId: string) => kinioFetch<{ rules: Rule[] }>(base(api, homeId)).then((r) => r?.rules ?? []),
  catalog: (api: string, homeId: string) => kinioFetch<RuleCatalog>(base(api, homeId) + '/catalog'),
  validate: (api: string, homeId: string, spec: RuleSpec) =>
    kinioFetch<{ ok: boolean; placement?: string; problems?: RuleProblem[] }>(base(api, homeId) + '/validate', { method: 'POST', body: spec }),
  create: (api: string, homeId: string, spec: RuleSpec) => kinioFetch<Rule>(base(api, homeId), { method: 'POST', body: spec }),
  put: (api: string, homeId: string, id: string, spec: RuleSpec & { version: number }) =>
    kinioFetch<Rule>(base(api, homeId) + '/' + encodeURIComponent(id), { method: 'PUT', body: spec }),
  patch: (api: string, homeId: string, id: string, body: { enabled?: boolean; name?: string }) =>
    kinioFetch<Rule>(base(api, homeId) + '/' + encodeURIComponent(id), { method: 'PATCH', body }),
  remove: (api: string, homeId: string, id: string) => kinioFetch(base(api, homeId) + '/' + encodeURIComponent(id), { method: 'DELETE' }),
  run: (api: string, homeId: string, id: string) =>
    kinioFetch<{ run_id?: string; placement: string }>(base(api, homeId) + '/' + encodeURIComponent(id) + '/run', { method: 'POST' }),
  runs: (api: string, homeId: string, id: string) =>
    kinioFetch<{ runs: RuleRun[] }>(base(api, homeId) + '/' + encodeURIComponent(id) + '/runs').then((r) => r?.runs ?? []),
};
